import React, { useContext, useState, useEffect, useMemo } from "react";
import { createLocationOfGoods, updateLocationOfGoods } from "../../../../api/locationOfGoods";
import elements from "../../../../constants/validation";
import DeclarationContext from "../../../../context/declaration/DeclarationContext";
import ConstantsBasedPullDownMenu from "../../../common/ConstantsBasedPullDownMenu";
import ConstantsBasedTextInput from "../../../common/ConstantsBasedTextInput";
import NotificationToast from "../../../common/NotificationToast";
import ThemeContext from "../../../../context/theme/ThemeContext";
import SectionActions from "./components/SectionActions";
import SectionSearch from "../SectionsSearch/SectionsSearch";

export const locationOfGoodsElements = [
	"locationOfGoodsId",
	"locationOfGoodsType",
	"locationOfGoodsCountry",
	"locationOfGoodsAddress",
	"locationOfGoodsCity",
	"locationOfGoodsPostCode",
	"contactRole",
	"qualifierOfIdentification",
	"goodslocation",
	"locationOfGoodsName",
];

const initialState = {
	locationOfGoodsId: "",
	locationOfGoodsCountry: "",
	locationOfGoodsCity: "",
	contactRole: "",
	contactsCountryList: "",
	locationOfGoodsPostCode: "",
	qualifierOfIdentification: "",
	goodslocation: "",
	locationOfGoodsName: "",
};

const LocationOfGoods = () => {
	const { setElementValueInSection, state, setHasUnsavedChanges } = useContext(DeclarationContext);
	const [saveConfirmation, setSaveConfirmation] = useState(false);
	const [isUpdateLoading, setIsUpdateLoading] = useState(false);
	const [isSaveLoading, setIsSaveLoading] = useState(false);
	const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
	const [searchValue, setSearchValue] = useState("");
	const [inputValues, setInputValues] = useState(initialState);
	const { withTheme } = useContext(ThemeContext);

	const handleSearchInputChange = (e) => {
		setSearchValue(e.target.value);
	};
	/**
	 * on the current user (after auth implementation) updating location of goods
	 */
	const handleUpdateExistingClick = () => {
		if (!inputValues.id) return;
		setIsUpdateLoading(true);

		const { id, ...data } = inputValues;
		updateLocationOfGoods(id, data)
			.then(() => {
				setIsUpdateLoading(false);
			})
			.catch((err) => {
				console.log(err);
				setIsUpdateLoading(false);
			});
	};
	/**
	 * on the current user (after auth implementation) adding a new location of goods
	 */
	const handleSaveClick = () => {
		setIsSaveLoading(true);
		createLocationOfGoods(inputValues).then((result) => {
			setInputValues({
				...inputValues,
				id: result.items && result.items[0] ? result.items[0].creationTime : inputValues.id,
			});
			setSaveConfirmation(true);
			setIsSaveLoading(false);
		});
	};
	/**
	 * on the current user (after auth implementation) (delete) clearing the location of goods
	 */
	const handleClearClick = () => {
		let updatedState = {};

		locationOfGoodsElements.forEach((element) => {
			updatedState[element] = "";
			setElementValueInSection("locationOfGoods", element, "");
		});

		setInputValues(updatedState);
	};

	const handleChange = (e) => {
		const { value, name } = e.target;
		let updatedState = {
			...inputValues,
			[name]: value,
		};
		setHasUnsavedChanges(true);

		setInputValues(updatedState);
		setElementValueInSection("locationOfGoods", name, value);
	};
	/**
	 * searching location of goods of an existing user by data prop
	 */
	const handleSearchSelection = (data) => {
		setIsSearchModalOpen(false);
		Object.keys(data).forEach((key) => {
			setElementValueInSection("locationOfGoods", key, data[key]);
		});
	};

	useEffect(() => {
		setInputValues(state.declaration.locationOfGoods || {});
	}, [state]);

	return (
		<div className={`${withTheme("lemon")} ${withTheme("inputs-section")}`}>
			<NotificationToast
				open={saveConfirmation}
				handleClose={() => setSaveConfirmation(false)}
				text="Location saved successfully"
			/>

			<div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
				<div className={withTheme("inputs-section-title")}>Location of Goods</div>

				<SectionSearch
					onSearchInputChange={handleSearchInputChange}
					searchValue={searchValue}
					setIsSearchModalOpen={setIsSearchModalOpen}
					isSearchModalOpen={isSearchModalOpen}
					searchResultElements={locationOfGoodsElements}
					onAddClick={handleSearchSelection}
					searchType="locationOfGoods"
				/>
			</div>

			<div style={{ display: "flex", flexWrap: "wrap", columnGap: 40, rowGap: 20 }}>
				{locationOfGoodsElements.map((element, index) => (
					<Element
						element={element}
						inputValues={inputValues}
						onChange={handleChange}
						key={index}
					/>
				))}
			</div>

			<SectionActions
				isUpdateLoading={isUpdateLoading}
				isSaveLoading={isSaveLoading}
				inputValues={inputValues}
				onUpdateExistingClick={handleUpdateExistingClick}
				onClearClick={handleClearClick}
				onSaveClick={handleSaveClick}
			/>
		</div>
	);
};

const Element = ({ element, inputValues, onChange }) => {
	const { state } = useContext(DeclarationContext);

	const validationElement = useMemo(
		() => elements.find((tmpElement) => tmpElement.elementName === element),
		[element]
	);

	if (element === "goodslocation" && state?.declaration?.service !== "export") {
		return <></>;
	}

	let Input = null;

	if (validationElement.pullDownMenu?.length) {
		Input = (
			<ConstantsBasedPullDownMenu
				onChange={onChange}
				value={inputValues[element]}
				element={validationElement}
			/>
		);
	} else {
		Input = (
			<ConstantsBasedTextInput
				onChange={onChange}
				value={element === "contactRole" ? "Location of Goods" : inputValues[element]}
				element={validationElement}
				disabled={element === "contactRole"}
			/>
		);
	}

	return <div style={{ width: "calc(50% - 40px)", flexGrow: 1 }}>{Input}</div>;
};

export default LocationOfGoods;
